/**
 * SettingsTab - Dataset settings with Configuration and Archive/Delete sub-tabs
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Tabs from '@amzn/awsui-components-console/tabs';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Alert from '@amzn/awsui-components-console/alert';
import Modal from '@amzn/awsui-components-console/modal';
import Input from '@amzn/awsui-components-console/input';
import FormField from '@amzn/awsui-components-console/form-field';
import { ConfigurationTab } from './ConfigurationTab';
import type { Dataset } from '../../../../types/starnexus';
import { datasetService } from '../../../../services/starnexus';

interface SettingsTabProps {
  dataset: Dataset;
  onUpdate: (dataset: Dataset) => void;
}

export const SettingsTab: React.FC<SettingsTabProps> = ({ dataset, onUpdate }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');

  const handleArchive = async () => {
    setLoading(true);
    try {
      const updated = await datasetService.updateStatus(dataset.datasetId, 'archived');
      if (updated) onUpdate(updated);
    } catch (err) {
      console.error('Failed to archive dataset', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async () => {
    setLoading(true);
    try {
      const updated = await datasetService.updateStatus(dataset.datasetId, 'active');
      if (updated) onUpdate(updated);
    } catch (err) {
      console.error('Failed to reactivate dataset', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (confirmInput !== dataset.datasetName) return;
    setLoading(true);
    try {
      await datasetService.deleteDataset(dataset.datasetId);
      navigate('/starnexus/uploader/datasets');
    } catch (err) {
      console.error('Failed to delete dataset', err);
    } finally {
      setLoading(false);
      setDeleteModalVisible(false);
      setConfirmInput('');
    }
  };

  return (
    <>
      <Tabs
        tabs={[
          {
            id: 'configuration',
            label: 'Configuration',
            content: <ConfigurationTab dataset={dataset} onUpdate={onUpdate} />
          },
          {
            id: 'archive-delete',
            label: 'Archive & Delete',
            content: (
              <SpaceBetween size="l">
                <Container header={<Header variant="h3">Archive & Reactivate</Header>}>
                  <SpaceBetween size="s">
                    <Box>
                      {dataset.status === 'archived'
                        ? 'This dataset is archived. Reactivating will restore it to active status.'
                        : 'Archiving will stop new uploads but keep all data and Andes tables intact. You can reactivate anytime.'}
                    </Box>
                    {dataset.status === 'archived' ? (
                      <Button onClick={handleReactivate} loading={loading}>Reactivate dataset</Button>
                    ) : (
                      <Button onClick={handleArchive} loading={loading} disabled={dataset.status !== 'active'}>Archive dataset</Button>
                    )}
                  </SpaceBetween>
                </Container>

                <Container header={<Header variant="h3"><Box color="text-status-error">Danger zone</Box></Header>}>
                  <SpaceBetween size="s">
                    <Alert type="warning" header="Delete permanently">
                      This will remove: DDB metadata, Glue table, S3 transformed data, and all batch history.
                      The Andes table is NOT auto-deleted (instructions will be provided after deletion). This cannot be undone.
                    </Alert>
                    <Button onClick={() => setDeleteModalVisible(true)} disabled={dataset.status === 'processing'}>
                      Delete permanently
                    </Button>
                  </SpaceBetween>
                </Container>
              </SpaceBetween>
            )
          }
        ]}
      />

      <Modal
        visible={deleteModalVisible}
        onDismiss={() => { setDeleteModalVisible(false); setConfirmInput(''); }}
        header="Delete dataset permanently"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => { setDeleteModalVisible(false); setConfirmInput(''); }}>Cancel</Button>
              <Button variant="primary" loading={loading} disabled={confirmInput !== dataset.datasetName} onClick={handleDelete}>Delete</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Alert type="warning">This action cannot be undone. All data, batch history, Glue table, and S3 files will be permanently removed.</Alert>
          <FormField label={`Type "${dataset.datasetName}" to confirm`}>
            <Input value={confirmInput} onChange={({ detail }) => setConfirmInput(detail.value)} placeholder={dataset.datasetName} />
          </FormField>
        </SpaceBetween>
      </Modal>
    </>
  );
};

export default SettingsTab;
